import { motion } from "framer-motion";
import {
    Sprout,
    Waves,
    Dumbbell,
    Coffee,
    Sun,
    Bike,
    Wifi,
    ParkingCircle,
    ShieldCheck,
    BookOpen,
    Trees,
    Utensils,
} from "lucide-react";

const AMENITIES = [
    { Icon: Trees, label: "Landscaped forest walk", detail: "1.8 km loop" },
    { Icon: Waves, label: "60m reflection pool", detail: "Solar-heated" },
    { Icon: Dumbbell, label: "Studio & gymnasium", detail: "Coach on request" },
    { Icon: Coffee, label: "Residents' café", detail: "Sun-lit atrium" },
    { Icon: Sun, label: "Rooftop deck", detail: "West-facing" },
    { Icon: Bike, label: "Bicycle library", detail: "12 e-bikes" },
    { Icon: BookOpen, label: "Reading pavilion", detail: "Silent hours 22–06" },
    { Icon: Utensils, label: "Community kitchen", detail: "Chef nights" },
    { Icon: Sprout, label: "Organic kitchen garden", detail: "Guided by chef" },
    { Icon: Wifi, label: "Fibre backbone", detail: "10 Gbps redundant" },
    { Icon: ParkingCircle, label: "EV charging bays", detail: "1:1 ratio" },
    { Icon: ShieldCheck, label: "24×7 concierge", detail: "In-app requests" },
];

const EASE = [0.22, 1, 0.36, 1];

export default function AmenitiesShowcase() {
    return (
        <section
            data-testid="amenities-section"
            className="relative bg-forest-950 text-ivory py-24 md:py-40"
        >
            <div className="mx-auto max-w-[1440px] px-6 md:px-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 mb-14 md:mb-20 items-end">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="h-px w-10 bg-lime" />
                            <span className="font-mono-mini text-[10px] tracking-[0.42em] uppercase text-lime">
                                Amenities · The daily
                            </span>
                        </div>
                        <h2 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-[16ch]">
                            Small rituals, engineered to feel effortless.
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:col-start-9 text-ivory/70 leading-relaxed">
                        Every KLiving community is designed around twelve daily amenities —
                        each detailed, sound-checked and lit by our in-house team.
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-0">
                    {AMENITIES.map((a, i) => (
                        <motion.div
                            key={a.label}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10% 0px" }}
                            transition={{ duration: 0.75, ease: EASE, delay: (i % 4) * 0.05 }}
                            data-testid={`amenity-${i}`}
                            className="group border-t border-white/10 [&:nth-child(4n)]:border-r-0 md:border-r border-white/10 p-8 md:p-10 hover:bg-forest-900/60 transition-colors"
                        >
                            <a.Icon
                                size={28}
                                strokeWidth={1.25}
                                className="text-lime mb-8 group-hover:-translate-y-1 transition-transform"
                            />
                            <div className="font-serif-display text-2xl leading-tight text-ivory">
                                {a.label}
                            </div>
                            <div className="mt-3 font-mono-mini text-[10px] tracking-[0.28em] uppercase text-ivory/50">
                                {a.detail}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
